import { SaveOperation } from '@infuse/api/common';
import { CreateUser, User } from '@infuse/api/iam/user';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericCreateUser = ({
  apiFn,
  roleApiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<Omit<CreateUser, 'roles'>, User>;
  roleApiFn: SaveOperation<{ roleIds: string[]; replace: boolean }, never>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; user: CreateUser }>({
    mutationFn: async ({ parentId, user }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        const createdUser = await apiFn(parentId ? { id: parentId } : {}, {
          name: user.name,
          email: user.email,
          locale: user.locale
        });
        await roleApiFn(
          { id: parentId, userId: createdUser.id },
          {
            roleIds: user.roles,
            replace: true
          }
        );

        notify({
          title: t('iam.users.header.create_user'),
          message: t('iam.users.notify.create_user_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.users.header.create_user'),
          message: getAxiosErrorMessage(e, t('iam.users.notify.create_user_failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  return {
    ...mutation
  };
};
